import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { usePageTracking, useMetricsIdentity } from '@byscott-io/metricsbyscott-web';
import {
  configureAuth,
  AuthProvider,
  useAuth,
  LoginForm,
  RegisterForm,
  ForgotPasswordForm,
  ResetPasswordForm,
  AcceptInvitationPage,
  ContainersPage,
  ProtectedRoute as AuthProtectedRoute,
  PublicRoute,
  MarketingRoute,
  PublicChat,
  useSpaLinkInterceptor,
  storage,
  FormGuardProvider,
  ErrorBoundary,
  UpdateAvailableBanner,
  VERSION_ANNOUNCED_EVENT,
} from '@byscott-io/corebyscott';
import { configureCable, PresenceProvider, useWaldoEvent } from '@byscott-io/where-is-waldo';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import FamiliesPage from './pages/FamiliesPage';
import FamilyPage from './pages/FamilyPage';
import StudentPage from './pages/StudentPage';
import AssessmentsPage from './pages/AssessmentsPage';
import AssessmentSessionPage from './pages/AssessmentSessionPage';
import AssessmentResultsPage from './pages/AssessmentResultsPage';
import StudentProgressPage from './pages/StudentProgressPage';
import GroupsPage from './pages/GroupsPage';
import GroupPage from './pages/GroupPage';
import GroupAnalyticsPage from './pages/GroupAnalyticsPage';
import PendingReviewPage from './pages/PendingReviewPage';
import ReviewDrawingsPage from './pages/ReviewDrawingsPage';
import AdminPage from './pages/AdminPage';
import SupportPage from './pages/SupportPage';
import ProfilePage from './pages/ProfilePage';

// Marketing pages
import LandingPage from './pages/marketing/LandingPage';
import FeaturesPage from './pages/marketing/FeaturesPage';
import PricingPage from './pages/marketing/PricingPage';
import AboutPage from './pages/marketing/AboutPage';
import ContactPage from './pages/marketing/ContactPage';
import HelpPage from './pages/marketing/HelpPage';

// Configure corebyscott for lifeskillspath
configureAuth({
  appName: 'LifeSkillsPath',
  containerName: 'account',
  containerLabel: 'Account',
  containerLabelPlural: 'Accounts',
  containerHeader: 'X-Account-Id',
  containerBasePath: '/app',
  dashboardPath: '/app',
  jwtIncludeContainer: true,
  roles: ['owner', 'admin', 'member'],
  adminRoles: ['owner', 'admin'],
  // Bot-check config for anonymous chat (#521) — read from layout meta tags.
  botCheckProvider:
    typeof document !== 'undefined'
      ? document.querySelector('meta[name="bot-check-provider"]')?.content || 'off'
      : 'off',
  turnstileSiteKey:
    typeof document !== 'undefined'
      ? document.querySelector('meta[name="turnstile-site-key"]')?.content || null
      : null,
  features: {
    invitations: true,
    passwordReset: true,
    sessionsManagement: true,
    multiContainer: true,
    registration: true,
    mfa: true,
  },
  footer: {
    enabled: true,
    brand: 'IdeasByScott.com',
    brandUrl: 'https://byscott.io',
    links: [],
  },
  marketing: {
    navItems: [
      { path: '/features', label: 'Features' },
      { path: '/pricing', label: 'Pricing' },
      { path: '/about', label: 'About' },
      { path: '/help', label: 'Help' },
      { path: '/contact', label: 'Contact' },
    ],
    footer: {
      companyName: 'IdeasByScott',
      companyDescription: 'Building practical tools for real-world problems.',
      productLinks: [
        { label: 'Features', href: '/features' },
        { label: 'Pricing', href: '/pricing' },
        { label: 'Help', href: '/help' },
      ],
      companyLinks: [
        { label: 'About', href: '/about' },
        { label: 'Contact', href: '/contact' },
      ],
      legalLinks: [
        { label: 'Privacy Policy', href: 'https://byscott.io/privacy-policy' },
        { label: 'Terms of Service', href: 'https://byscott.io/terms' },
      ],
      copyright: `© ${new Date().getFullYear()} IdeasByScott. All rights reserved.`,
    },
  },
  logo: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7">
      <path d="M11.7 2.805a.75.75 0 01.6 0A60.65 60.65 0 0122.83 8.72a.75.75 0 01-.231 1.337 49.949 49.949 0 00-9.902 3.912l-.003.002-.34.18a.75.75 0 01-.707 0A50.009 50.009 0 007.5 12.174v-.224c0-.131.067-.248.172-.311a54.614 54.614 0 014.653-2.52.75.75 0 00-.65-1.352 56.129 56.129 0 00-4.78 2.589 1.858 1.858 0 00-.859 1.228 49.803 49.803 0 00-4.634-1.527.75.75 0 01-.231-1.337A60.653 60.653 0 0111.7 2.805z" />
      <path d="M13.06 15.473a48.45 48.45 0 017.666-3.282c.134 1.414.22 2.843.255 4.285a.75.75 0 01-.46.71 47.878 47.878 0 00-8.105 4.342.75.75 0 01-.832 0 47.877 47.877 0 00-8.104-4.342.75.75 0 01-.461-.71c.035-1.442.121-2.87.255-4.286A48.4 48.4 0 016 13.18v1.27a1.5 1.5 0 00-.14 2.508c-.09.38-.222.753-.397 1.11.452.213.901.434 1.346.661a6.729 6.729 0 00.551-1.608 1.5 1.5 0 00.14-2.67v-.645a48.549 48.549 0 013.44 1.668 2.25 2.25 0 002.12 0z" />
      <path d="M4.462 19.462c.42-.419.753-.89 1-1.394.453.213.902.434 1.347.661a6.743 6.743 0 01-1.286 1.794.75.75 0 11-1.06-1.06z" />
    </svg>
  ),
});

function PageTracker() {
  const location = useLocation();
  usePageTracking(location.pathname);
  return null;
}

// Sync authenticated identity (user_id / member_id / container_id) into the
// metrics client. Boot-seed (application.js) covers the pre-/me window.
function MetricsIdentity() {
  useMetricsIdentity(useAuth());
  return null;
}

function ProtectedRoute({ children }) {
  return (
    <AuthProtectedRoute>
      <Layout>{children}</Layout>
    </AuthProtectedRoute>
  );
}

function SpaLinkInterceptor({ children }) {
  useSpaLinkInterceptor();
  return children;
}

// Real-time cable connection (#626): JWT auth via corebyscott storage token.
configureCable({
  url: '/cable',
  getToken: () => storage.getToken(),
});

// TWO BOUNDARIES, doing different jobs. This inner one is inside the router and
// resets on navigation, so a page that throws is recovered by going elsewhere.
// The outer one in App sits OUTSIDE the providers, so it also catches a crash
// in a provider, which this one cannot -- it mounts underneath them. Without
// either, an uncaught render error unmounts the tree and leaves a blank page.
function RouteBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

// A deploy announces itself over waldo when the new container boots. Core
// cannot subscribe for itself -- it must not import the waldo client, since not
// every app has one -- so the app turns the broadcast into the window event
// core listens for. The payload is not used: it only prompts a fresh ask of
// /api/version, which is what actually decides.
function VersionAnnouncements() {
  useWaldoEvent('app_version', () => {
    window.dispatchEvent(new Event(VERSION_ANNOUNCED_EVENT));
  });
  return null;
}

export default function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <PresenceProvider>
      <FormGuardProvider>
      <BrowserRouter>
        {/* Offers a refresh when a deploy has replaced the code this tab is
            running. Never reloads on its own. */}
        <UpdateAvailableBanner />
        <VersionAnnouncements />
        <PageTracker />
        <MetricsIdentity />
        <SpaLinkInterceptor>
        <RouteBoundary>
        <Routes>
          {/* Marketing Pages (public, no redirect for logged-in users) */}
          <Route
            path="/"
            element={
              <MarketingRoute>
                <LandingPage />
              </MarketingRoute>
            }
          />
          <Route
            path="/features"
            element={
              <MarketingRoute>
                <FeaturesPage />
              </MarketingRoute>
            }
          />
          <Route
            path="/pricing"
            element={
              <MarketingRoute>
                <PricingPage />
              </MarketingRoute>
            }
          />
          <Route
            path="/about"
            element={
              <MarketingRoute>
                <AboutPage />
              </MarketingRoute>
            }
          />
          <Route
            path="/contact"
            element={
              <MarketingRoute>
                <ContactPage />
              </MarketingRoute>
            }
          />
          <Route
            path="/help/*"
            element={
              <MarketingRoute>
                <HelpPage />
              </MarketingRoute>
            }
          />

          {/* Auth Pages (redirect logged-in users to /app) */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginForm />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <RegisterForm />
              </PublicRoute>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicRoute>
                <ForgotPasswordForm />
              </PublicRoute>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicRoute>
                <ResetPasswordForm />
              </PublicRoute>
            }
          />
          <Route
            path="/invitations/accept/:token"
            element={<AcceptInvitationPage />}
          />

          {/* App Pages (protected, requires auth) */}
          <Route
            path="/app"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/families"
            element={
              <ProtectedRoute>
                <FamiliesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/families/:id"
            element={
              <ProtectedRoute>
                <FamilyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/students/:id"
            element={
              <ProtectedRoute>
                <StudentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/students/:studentId/assessments"
            element={
              <ProtectedRoute>
                <AssessmentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/students/:studentId/progress"
            element={
              <ProtectedRoute>
                <StudentProgressPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/assessments/:id"
            element={
              <ProtectedRoute>
                <AssessmentSessionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/assessments/:id/results"
            element={
              <ProtectedRoute>
                <AssessmentResultsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/assessments/:id/review"
            element={
              <ProtectedRoute>
                <ReviewDrawingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/reviews"
            element={
              <ProtectedRoute>
                <PendingReviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/groups"
            element={
              <ProtectedRoute>
                <GroupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/groups/:id"
            element={
              <ProtectedRoute>
                <GroupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/groups/:groupId/analytics"
            element={
              <ProtectedRoute>
                <GroupAnalyticsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/support"
            element={
              <ProtectedRoute>
                <SupportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app/accounts"
            element={
              <ProtectedRoute>
                <ContainersPage />
              </ProtectedRoute>
            }
          />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </RouteBoundary>
        </SpaLinkInterceptor>
        <PublicChat
          authenticatedTask={{
            taskType: 'LifeSkillsPathChatTask',
            title: 'LifeSkillsPath AI',
            placeholder: 'Ask about students, assessments, or skill gaps...',
            welcomeMessage:
              "Hi! I can help you understand a student's progress, identify skill gaps, and choose the next assessment.",
            voiceEnabled: true,
            attachmentsEnabled: true,
          }}
          anonymousTask={{
            taskType: 'SupportChatTask',
            title: 'LifeSkillsPath AI',
            welcomeMessage:
              'Hi! I can help you figure out if LifeSkillsPath fits — what grade are you assessing for, or what are you trying to track?',
            placeholder: 'Ask about assessments, pricing, fit…',
          }}
        />
      </BrowserRouter>
      </FormGuardProvider>
      </PresenceProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}
